import React, { useState, useEffect, useContext } from 'react';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROLE_PERMISSIONS } from '../config/permissions';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Group,
  Settings,
  Security,
  Description,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';

// Import UserManagement from the projects folder where you moved it
import UserManagement from '../components/projects/UserManagement';

// Helper to get month names
const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Helper to get years (e.g., last 5 years)
const getYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < 5; i++) {
    years.push(currentYear - i);
  }
  return years;
};

export default function AdminPanel() {
  const { user, role } = useContext(AuthContext);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [adminError, setAdminError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [adminDocs, setAdminDocs] = useState([]);
  const [loadingAdminDocs, setLoadingAdminDocs] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const availableYears = getYears();
  const [adminSubTab, setAdminSubTab] = useState(0);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [updatingBatch, setUpdatingBatch] = useState(false);

  // Omzet berekening constants
  const PRICE_PER_DOCUMENT = 10.50;
  const MONTHLY_BASE_FEE = 300.00;
  
  // Financial dashboard state
  const [financialData, setFinancialData] = useState([]);
  const [loadingFinancialData, setLoadingFinancialData] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(3); // Default to 3 months
  const [yearlyTotal, setYearlyTotal] = useState(null);

  const muiTheme = useMuiTheme();

  // Debug logging
  useEffect(() => {
    console.log('AdminPanel - Current user:', user);
    console.log('AdminPanel - Current role:', role);
  }, [user, role]);

  // Update admin check
  useEffect(() => {
    if (user === null) return;

    if (!user || role !== 'admin') {
      console.log('Access denied - User:', user, 'Role:', role);
      navigate('/');
    }
  }, [user, role, navigate]);

  // Fetch financial data for selected period
  const fetchFinancialData = async (months = 3) => {
    setLoadingFinancialData(true);
    try {
      const currentDate = new Date();
      const monthsData = [];
      
      // Get data for selected number of months
      for (let i = 0; i < months; i++) {
        const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const startDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
        const endDate = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1);
        endDate.setMilliseconds(endDate.getMilliseconds() - 1);

        const { data, error } = await supabase
          .from('document_admin')
          .select('id, name, status, gefactureerd, created_at')
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());

        if (error) throw error;

        const exportedDocs = (data || []).filter(doc => doc.status === 'exported');
        const totalDocs = data?.length || 0;
        const billableDocs = exportedDocs.filter(doc => !doc.gefactureerd).length;
        const billedDocs = exportedDocs.filter(doc => doc.gefactureerd).length;
        const documentRevenue = exportedDocs.length * PRICE_PER_DOCUMENT;
        const totalRevenue = documentRevenue + MONTHLY_BASE_FEE;

        monthsData.push({
          month: monthDate.getMonth(),
          year: monthDate.getFullYear(),
          monthName: monthNames[monthDate.getMonth()],
          totalDocs,
          exportedDocs: exportedDocs.length,
          billableDocs,
          billedDocs,
          documentRevenue,
          totalRevenue,
          rawData: data || []
        });
      }
      
      setFinancialData(monthsData);
      
      // Also fetch yearly total
      await fetchYearlyTotal();
      
    } catch (error) {
      console.error('Error fetching financial data:', error);
      setError('Fout bij ophalen financiële gegevens: ' + error.message);
    } finally {
      setLoadingFinancialData(false);
    }
  };

  // Fetch yearly total
  const fetchYearlyTotal = async () => {
    try {
      const currentYear = new Date().getFullYear();
      // Start vanaf april 2024 (maand 3, want 0-indexed)
      const startOfBusiness = new Date(currentYear, 3, 1); // April 1st
      const endOfYear = new Date(currentYear + 1, 0, 1);
      endOfYear.setMilliseconds(endOfYear.getMilliseconds() - 1);

      const { data, error } = await supabase
        .from('document_admin')
        .select('id, name, status, gefactureerd, created_at')
        .gte('created_at', startOfBusiness.toISOString())
        .lte('created_at', endOfYear.toISOString());

      if (error) throw error;

      const exportedDocs = (data || []).filter(doc => doc.status === 'exported');
      const totalDocs = data?.length || 0;
      const billableDocs = exportedDocs.filter(doc => !doc.gefactureerd).length;
      const billedDocs = exportedDocs.filter(doc => doc.gefactureerd).length;
      
      // Calculate monthly base fee for completed months since April
      const currentMonth = new Date().getMonth() + 1; // 1-12
      const businessMonths = Math.max(0, currentMonth - 3); // April = month 4, so subtract 3
      const monthlyFees = businessMonths * MONTHLY_BASE_FEE;
      
      const documentRevenue = exportedDocs.length * PRICE_PER_DOCUMENT;
      const totalRevenue = documentRevenue + monthlyFees;

      setYearlyTotal({
        year: currentYear,
        totalDocs,
        exportedDocs: exportedDocs.length,
        billableDocs,
        billedDocs,
        documentRevenue,
        totalRevenue,
        monthlyFees,
        completedMonths: businessMonths,
        startMonth: 'April' // Voor weergave
      });

    } catch (error) {
      console.error('Error fetching yearly total:', error);
      setError('Fout bij ophalen jaargegevens: ' + error.message);
    }
  };

  // Update useEffect to use selected period
  useEffect(() => {
    fetchFinancialData(selectedPeriod);
  }, [selectedPeriod]);

  // Load financial data on component mount
  useEffect(() => {
    fetchFinancialData(selectedPeriod);
  }, []);

  // Fetch admin document data based on selected month/year
  const fetchDocumentAdminData = async (month, year) => {
    setLoadingAdminDocs(true);
    setAdminError(null);

    try {
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 1);
      endDate.setMilliseconds(endDate.getMilliseconds() - 1);

      const { data, error } = await supabase
        .from('document_admin')
        .select('id, name, status, gefactureerd, created_at')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      setAdminDocs(data || []);

    } catch (error) {
      console.error('Error fetching admin document data:', error);
      setAdminError('Fout bij ophalen administratie gegevens: ' + error.message);
      setAdminDocs([]);
    } finally {
      setLoadingAdminDocs(false);
      setSelectedDocIds([]);
    }
  };

  // Toggle 'gefactureerd' status
  const handleToggleGefactureerd = async (docId, currentStatus) => {
    const newValue = !currentStatus;
    setAdminDocs(prevDocs =>
      prevDocs.map(doc =>
        doc.id === docId ? { ...doc, gefactureerd: newValue } : doc
      )
    );

    try {
      const { error } = await supabase
        .from('document_admin')
        .update({ gefactureerd: newValue })
        .eq('id', docId);

      if (error) {
        setAdminDocs(prevDocs =>
          prevDocs.map(doc =>
            doc.id === docId ? { ...doc, gefactureerd: currentStatus } : doc
          )
        );
        throw error;
      }
    } catch (error) {
      console.error('Error updating gefactureerd status:', error);
      setAdminError('Kon status niet bijwerken: ' + error.message);
    }
  };

  // Handle selection changes
  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedDocIds.indexOf(id);
    let newSelectedDocIds = [];

    if (selectedIndex === -1) {
      newSelectedDocIds = newSelectedDocIds.concat(selectedDocIds, id);
    } else if (selectedIndex === 0) {
      newSelectedDocIds = newSelectedDocIds.concat(selectedDocIds.slice(1));
    } else if (selectedIndex === selectedDocIds.length - 1) {
      newSelectedDocIds = newSelectedDocIds.concat(selectedDocIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedDocIds = newSelectedDocIds.concat(
        selectedDocIds.slice(0, selectedIndex),
        selectedDocIds.slice(selectedIndex + 1),
      );
    }
    setSelectedDocIds(newSelectedDocIds);
  };

  const handleSelectAll = (event, docsToSelect) => {
    if (event.target.checked) {
      const newSelecteds = docsToSelect.map((n) => n.id);
      setSelectedDocIds(newSelecteds);
      return;
    }
    setSelectedDocIds([]);
  };

  // Handle batch update to gefactureerd
  const handleBatchUpdateGefactureerd = async () => {
    if (selectedDocIds.length === 0) {
      setAdminError("Selecteer eerst documenten om te markeren.");
      return;
    }
    setUpdatingBatch(true);
    setAdminError(null);

    try {
      const { error } = await supabase
        .from('document_admin')
        .update({ gefactureerd: true })
        .in('id', selectedDocIds);

      if (error) throw error;

      const updatedDocs = adminDocs.map(doc =>
        selectedDocIds.includes(doc.id)
          ? { ...doc, gefactureerd: true }
          : doc
      );
      setAdminDocs(updatedDocs);
      setSelectedDocIds([]);

    } catch (error) {
      console.error('Error batch updating gefactureerd status:', error);
      setAdminError('Batch update mislukt: ' + error.message);
    } finally {
      setUpdatingBatch(false);
    }
  };

  // Fetch data when tab becomes active or month/year changes
  useEffect(() => {
    if (activeTab === 3 && role === 'admin') {
      fetchDocumentAdminData(selectedMonth, selectedYear);
    }
    if (activeTab === 4 && role === 'admin') {
      fetchFinancialData();
    }
    if (![3, 4].includes(activeTab)) {
      setAdminSubTab(0);
    }
  }, [activeTab, selectedMonth, selectedYear, role]);

  // Calculate monthly revenue
  const calculateMonthlyRevenue = () => {
    const exportedDocs = adminDocs.filter(doc => doc.status === 'exported');
    const billableDocs = exportedDocs.filter(doc => !doc.gefactureerd); // Only unbilled exported docs
    const totalBillableDocs = exportedDocs.length; // All exported docs for total calculation
    const documentRevenue = totalBillableDocs * PRICE_PER_DOCUMENT;
    const totalRevenue = documentRevenue + MONTHLY_BASE_FEE;
    
    return {
      totalDocs: adminDocs.length,
      exportedDocs: exportedDocs.length,
      billableDocs: billableDocs.length,
      billedDocs: exportedDocs.filter(doc => doc.gefactureerd).length,
      documentRevenue,
      baseFee: MONTHLY_BASE_FEE,
      totalRevenue
    };
  };

  // Filter documents based on the selected sub-tab
  const getFilteredAdminDocs = () => {
    switch (adminSubTab) {
      case 0:
        return adminDocs.filter(doc => doc.status === 'intake');
      case 1:
        return adminDocs.filter(doc => ['to_review', 'reviewed'].includes(doc.status));
      case 2:
        return adminDocs.filter(doc => doc.status === 'exported' && !doc.gefactureerd); // Factureerbaar
      case 3:
        return adminDocs.filter(doc => ['failed', 'faulted'].includes(doc.status));
      case 4:
        return adminDocs.filter(doc => doc.status === 'exported' && doc.gefactureerd); // Gefactureerd
      default:
        return adminDocs;
    }
  };

  const filteredDocs = getFilteredAdminDocs();
  const revenueData = calculateMonthlyRevenue();

  // Financial dashboard calculations
  const getGrowthPercentage = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100);
  };

  const getGrowthIcon = (percentage) => {
    if (percentage > 0) return '📈';
    if (percentage < 0) return '📉';
    return '➡️';
  };

  const getGrowthColor = (percentage) => {
    if (percentage > 0) return 'success.main';
    if (percentage < 0) return 'error.main';
    return 'grey.500';
  };

  // Handle CSV Export
  const handleExportCSV = () => {
    if (filteredDocs.length === 0) {
      setAdminError("Geen documenten om te exporteren.");
      return;
    }

    // Define headers
    const headers = ["Naam", "Status", "Gefactureerd", "Aangemaakt op"];
    // Format data rows
    const rows = filteredDocs.map(doc => [
      `"${doc.name?.replace(/"/g, '""') || 'N/A'}"`, // Escape double quotes
      `"${doc.status || 'N/A'}"`, // Ensure strings are quoted
      doc.gefactureerd ? '"Ja"' : '"Nee"' ,
      `"${doc.created_at ? new Date(doc.created_at).toLocaleString() : 'N/A'}"`
    ].join(',')); // Join cells with commas

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create Blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    // Construct filename based on filters
    const subTabName = ['Geupload', 'Verwerkt', 'Factureerbaar', 'Gefaald', 'Gefactureerd'][adminSubTab];
    const monthString = (selectedMonth + 1).toString().padStart(2, '0');
    const filename = `documenten_${selectedYear}-${monthString}_${subTabName}.csv`;
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper elevation={0} sx={{
        background: `linear-gradient(135deg, ${muiTheme.palette.primary.main} 0%, ${muiTheme.palette.primary.dark} 100%)`,
        color: 'primary.contrastText',
        py: 4,
        px: { xs: 2, md: 4 },
        mb: 2,
        borderRadius: 0,
        textAlign: 'center',
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        minWidth: '100vw',
        maxWidth: '100vw',
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Admin Panel
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Beheer gebruikers, rollen en systeeminstellingen
        </Typography>
      </Paper>
      <Container maxWidth="xl" sx={{ mt: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper elevation={3} sx={{ p: 3, width: '100%' }}>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Admin Panel Tabs"
        >
          <Tab label="Gebruikers" icon={<Group />} iconPosition="start" />
          <Tab label="Rollen & Permissies" icon={<Security />} iconPosition="start" />
          <Tab label="Systeem" icon={<Settings />} iconPosition="start" />
          <Tab label="Administratie" icon={<Description />} iconPosition="start" />
          <Tab label="💰 Financieel Dashboard" iconPosition="start" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <UserManagement />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Rol Permissies Overzicht
            </Typography>
            {Object.entries(ROLE_PERMISSIONS).map(([role, permissions]) => (
              <Card key={`role-${role}`} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ textTransform: 'capitalize', mb: 1 }}>
                    {role}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {permissions.map((permission) => (
                      <Chip
                        key={`${role}-${permission}`}
                        label={permission}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Systeem Instellingen
            </Typography>
            <Card>
              <CardContent>
                <Typography>
                  Systeem instellingen komen binnenkort beschikbaar.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Document Administratie
            </Typography>

            {/* Omzet Overzicht */}
            <Card sx={{ mb: 3, background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                  💰 Omzet {monthNames[selectedMonth]} {selectedYear}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                        €{revenueData.totalRevenue.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Totale Omzet
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ color: 'white' }}>
                        {revenueData.exportedDocs}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Geëxporteerde Documenten
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        €{revenueData.documentRevenue.toFixed(2)} ({revenueData.exportedDocs} × €{PRICE_PER_DOCUMENT})
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ color: 'white' }}>
                        {revenueData.billableDocs}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Nog Te Factureren
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        €{(revenueData.billableDocs * PRICE_PER_DOCUMENT).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ color: 'white' }}>
                        €{MONTHLY_BASE_FEE.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Standaard Maandbedrag
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        Vaste kosten
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Grid container spacing={2} sx={{ mb: 3, alignItems: 'flex-end' }}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="month-select-label">Maand</InputLabel>
                  <Select
                    labelId="month-select-label"
                    value={selectedMonth}
                    label="Maand"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    {monthNames.map((name, index) => (
                      <MenuItem key={name} value={index}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">Jaar</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={selectedYear}
                    label="Jaar"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleExportCSV}
                  disabled={filteredDocs.length === 0 || loadingAdminDocs}
                  fullWidth
                >
                  Exporteer naar CSV
                </Button>
              </Grid>
              {adminSubTab === 2 && ( // Only show for "Factureerbaar" tab
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleBatchUpdateGefactureerd}
                    disabled={selectedDocIds.length === 0 || loadingAdminDocs || updatingBatch}
                    fullWidth
                  >
                    {updatingBatch ? 'Bezig...' : `Factureer ${selectedDocIds.length} Document(en)`}
                  </Button>
                </Grid>
              )}
            </Grid>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={adminSubTab}
                onChange={(e, newValue) => setAdminSubTab(newValue)}
                aria-label="Document Status Filter Tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab 
                  label={`Geupload (${adminDocs.filter(doc => doc.status === 'intake').length})`} 
                  sx={{ minWidth: 120 }}
                />
                <Tab 
                  label={`Verwerkt (${adminDocs.filter(doc => ['to_review', 'reviewed'].includes(doc.status)).length})`} 
                  sx={{ minWidth: 120 }}
                />
                <Tab 
                  label={`💰 Factureerbaar (${revenueData.billableDocs})`} 
                  sx={{ minWidth: 150, color: revenueData.billableDocs > 0 ? 'success.main' : 'inherit' }}
                />
                <Tab 
                  label={`Gefaald (${adminDocs.filter(doc => ['failed', 'faulted'].includes(doc.status)).length})`} 
                  sx={{ minWidth: 120 }}
                />
                <Tab 
                  label={`✅ Gefactureerd (${revenueData.billedDocs})`} 
                  sx={{ minWidth: 150 }}
                />
              </Tabs>
            </Box>

            {adminError && <Alert severity="error" sx={{ mb: 2 }}>{adminError}</Alert>}

            {loadingAdminDocs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={1}>
                <Table sx={{ minWidth: 650 }} aria-label="document administration table">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                      <TableCell padding="checkbox">
                        {adminSubTab === 2 && filteredDocs.length > 0 && ( // Only for "Factureerbaar" tab
                          <Checkbox
                            color="primary"
                            indeterminate={
                              selectedDocIds.length > 0 &&
                              selectedDocIds.length < filteredDocs.length
                            }
                            checked={
                              filteredDocs.length > 0 &&
                              selectedDocIds.length === filteredDocs.length
                            }
                            onChange={(e) => handleSelectAll(e, filteredDocs)}
                            inputProps={{
                              'aria-label': 'select all documents on this page',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>Naam</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Gefactureerd</TableCell>
                      <TableCell>Aangemaakt op</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDocs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          {adminDocs.length === 0
                            ? "Geen documenten gevonden voor deze periode."
                            : "Geen documenten met deze status gevonden."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDocs.map((doc) => {
                        const isItemSelected = selectedDocIds.indexOf(doc.id) !== -1;
                        const labelId = `checkbox-list-label-${doc.id}`;

                        return (
                          <TableRow
                            hover
                            onClick={(event) => {adminSubTab === 2 && handleSelectOne(event, doc.id)}}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={doc.id}
                            selected={isItemSelected}
                            sx={{ 
                              '&:last-child td, &:last-child th': { border: 0 }, 
                              cursor: adminSubTab === 2 ? 'pointer' : 'default',
                              backgroundColor: adminSubTab === 2 && doc.status === 'exported' && !doc.gefactureerd 
                                ? 'rgba(76, 175, 80, 0.1)' : 'inherit' // Light green for billable docs
                            }}
                          >
                            <TableCell padding="checkbox">
                              {adminSubTab === 2 && ( // Only for "Factureerbaar" tab
                                <Checkbox
                                  color="primary"
                                  checked={isItemSelected}
                                  inputProps={{
                                    'aria-labelledby': labelId,
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell component="th" id={labelId} scope="row">
                              {doc.name || 'N/A'}
                            </TableCell>
                            <TableCell>{doc.status || 'N/A'}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={!!doc.gefactureerd}
                                onChange={() => handleToggleGefactureerd(doc.id, !!doc.gefactureerd)}
                                disabled={adminSubTab === 4 || doc.status !== 'exported'} // Disable for "Gefactureerd" tab or non-exported docs
                                color={doc.status === 'exported' && !doc.gefactureerd ? 'success' : 'default'}
                                inputProps={{ 'aria-label': `Markeer ${doc.name || 'document'} als gefactureerd` }}
                              />
                              {doc.status === 'exported' && !doc.gefactureerd && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                  €{PRICE_PER_DOCUMENT}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {doc.created_at ? new Date(doc.created_at).toLocaleString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              💰 Financieel Dashboard
            </Typography>

            {/* Period Selection */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="period-select-label">Periode</InputLabel>
                  <Select
                    labelId="period-select-label"
                    value={selectedPeriod}
                    label="Periode"
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                  >
                    <MenuItem value={1}>Laatste 1 maand</MenuItem>
                    <MenuItem value={3}>Laatste 3 maanden</MenuItem>
                    <MenuItem value={6}>Laatste 6 maanden</MenuItem>
                    <MenuItem value={12}>Laatste 12 maanden</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {loadingFinancialData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography sx={{ ml: 2, alignSelf: 'center' }}>Financiële gegevens laden...</Typography>
              </Box>
            ) : (
              <>
                {/* Yearly Total Overview */}
                {yearlyTotal && (
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                      <Card sx={{ 
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)', 
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                                                 <CardContent sx={{ pb: 3 }}>
                           <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                             🏆 Totaal {yearlyTotal.year} (vanaf {yearlyTotal.startMonth})
                           </Typography>
                          <Grid container spacing={4}>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  €{yearlyTotal.totalRevenue.toFixed(2)}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                  Totale Jaaromzet
                                </Typography>
                                                                 <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                   {yearlyTotal.completedMonths} maanden sinds april
                                 </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  {yearlyTotal.exportedDocs}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                  Geëxporteerde Docs
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                  €{yearlyTotal.documentRevenue.toFixed(2)} aan docs
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  €{((yearlyTotal.billableDocs) * PRICE_PER_DOCUMENT).toFixed(2)}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                  Openstaand Totaal
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                  {yearlyTotal.billableDocs} docs te factureren
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  €{yearlyTotal.monthlyFees.toFixed(2)}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                  Basis Tarieven
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                  {yearlyTotal.completedMonths} × €{MONTHLY_BASE_FEE}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {financialData.length > 0 ? (
                  <>
                    {/* Key Performance Indicators */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12}>
                        <Card sx={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                          color: 'white',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <CardContent sx={{ pb: 3 }}>
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                              📊 Prestatie Overzicht - Laatste {selectedPeriod} maand{selectedPeriod > 1 ? 'en' : ''}
                            </Typography>
                            <Grid container spacing={4}>
                              <Grid item xs={12} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    €{selectedPeriod === 1 
                                      ? (financialData[0]?.totalRevenue?.toFixed(2) || '0.00')
                                      : financialData.reduce((sum, month) => sum + (month.totalRevenue || 0), 0).toFixed(2)
                                    }
                                  </Typography>
                                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    {selectedPeriod === 1 ? 'Huidige Maand Omzet' : `Totale Omzet ${selectedPeriod} Maanden`}
                                  </Typography>
                                  {financialData[1] && selectedPeriod === 1 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                                      <Typography variant="body2" sx={{ 
                                        color: getGrowthPercentage(financialData[0]?.totalRevenue || 0, financialData[1]?.totalRevenue || 0) >= 0 
                                          ? '#4caf50' : '#f44336',
                                        fontWeight: 'bold'
                                      }}>
                                        {getGrowthIcon(getGrowthPercentage(financialData[0]?.totalRevenue || 0, financialData[1]?.totalRevenue || 0))}
                                        {Math.abs(getGrowthPercentage(financialData[0]?.totalRevenue || 0, financialData[1]?.totalRevenue || 0)).toFixed(1)}%
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {selectedPeriod === 1 
                                      ? (financialData[0]?.exportedDocs || 0)
                                      : financialData.reduce((sum, month) => sum + (month.exportedDocs || 0), 0)
                                    }
                                  </Typography>
                                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Geëxporteerde Docs
                                  </Typography>
                                  {financialData[1] && selectedPeriod === 1 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                                      <Typography variant="body2" sx={{ 
                                        color: getGrowthPercentage(financialData[0]?.exportedDocs || 0, financialData[1]?.exportedDocs || 0) >= 0 
                                          ? '#4caf50' : '#f44336',
                                        fontWeight: 'bold'
                                      }}>
                                        {getGrowthIcon(getGrowthPercentage(financialData[0]?.exportedDocs || 0, financialData[1]?.exportedDocs || 0))}
                                        {Math.abs(getGrowthPercentage(financialData[0]?.exportedDocs || 0, financialData[1]?.exportedDocs || 0)).toFixed(1)}%
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    €{selectedPeriod === 1 
                                      ? ((financialData[0]?.billableDocs || 0) * PRICE_PER_DOCUMENT).toFixed(2)
                                      : (financialData.reduce((sum, month) => sum + (month.billableDocs || 0), 0) * PRICE_PER_DOCUMENT).toFixed(2)
                                    }
                                  </Typography>
                                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Openstaand Bedrag
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                    {selectedPeriod === 1 
                                      ? (financialData[0]?.billableDocs || 0)
                                      : financialData.reduce((sum, month) => sum + (month.billableDocs || 0), 0)
                                    } docs × €{PRICE_PER_DOCUMENT}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {(() => {
                                      const totalBilled = selectedPeriod === 1 
                                        ? (financialData[0]?.billedDocs || 0)
                                        : financialData.reduce((sum, month) => sum + (month.billedDocs || 0), 0);
                                      const totalExported = selectedPeriod === 1 
                                        ? (financialData[0]?.exportedDocs || 0)
                                        : financialData.reduce((sum, month) => sum + (month.exportedDocs || 0), 0);
                                      return totalExported > 0 ? ((totalBilled / totalExported) * 100).toFixed(0) : 0;
                                    })()}%
                                  </Typography>
                                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Facturatie Ratio
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                    {selectedPeriod === 1 
                                      ? (financialData[0]?.billedDocs || 0)
                                      : financialData.reduce((sum, month) => sum + (month.billedDocs || 0), 0)
                                    } van {selectedPeriod === 1 
                                      ? (financialData[0]?.exportedDocs || 0)
                                      : financialData.reduce((sum, month) => sum + (month.exportedDocs || 0), 0)
                                    } gefactureerd
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Monthly Comparison Chart */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              📈 Maandelijkse Trends - Laatste {selectedPeriod} maand{selectedPeriod > 1 ? 'en' : ''}
                            </Typography>
                            <Box sx={{ height: 300, display: 'flex', alignItems: 'end', gap: selectedPeriod > 6 ? 1 : 3, mt: 3, overflowX: 'auto' }}>
                              {financialData.slice().reverse().map((month, index) => {
                                const maxRevenue = Math.max(...financialData.map(m => m.totalRevenue));
                                const heightPercentage = (month.totalRevenue / maxRevenue) * 100;
                                
                                // Dynamic gradient colors based on position
                                const getGradientColor = (idx) => {
                                  const colors = [
                                    'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                                    'linear-gradient(45deg, #4ecdc4, #44a08d)',
                                    'linear-gradient(45deg, #a8e6cf, #7fcdcd)',
                                    'linear-gradient(45deg, #ffd93d, #ffb74d)',
                                    'linear-gradient(45deg, #667eea, #764ba2)',
                                    'linear-gradient(45deg, #f093fb, #f5576c)',
                                    'linear-gradient(45deg, #4facfe, #00f2fe)',
                                    'linear-gradient(45deg, #43e97b, #38f9d7)',
                                    'linear-gradient(45deg, #fa709a, #fee140)',
                                    'linear-gradient(45deg, #a8edea, #fed6e3)',
                                    'linear-gradient(45deg, #ff9a9e, #fecfef)',
                                    'linear-gradient(45deg, #ffecd2, #fcb69f)'
                                  ];
                                  return colors[idx % colors.length];
                                };
                                
                                return (
                                  <Box key={`${month.year}-${month.month}`} sx={{ 
                                    flex: selectedPeriod > 6 ? '0 0 80px' : 1, 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: selectedPeriod > 6 ? '80px' : 'auto'
                                  }}>
                                    {/* Revenue Bar */}
                                    <Box sx={{ 
                                      width: '100%', 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      height: 250
                                    }}>
                                      <Typography variant={selectedPeriod > 6 ? "body2" : "h6"} sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                                        €{month.totalRevenue.toFixed(0)}
                                      </Typography>
                                      <Box sx={{
                                        width: '80%',
                                        height: `${heightPercentage}%`,
                                        background: getGradientColor(index),
                                        borderRadius: '8px 8px 0 0',
                                        display: 'flex',
                                        alignItems: 'end',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          transform: 'scaleY(1.05)',
                                          boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                                        }
                                      }}>
                                        <Typography variant="caption" sx={{ mb: 1, writingMode: 'vertical-rl', textAlign: 'center' }}>
                                          {month.exportedDocs} docs
                                        </Typography>
                                      </Box>
                                    </Box>
                                    
                                    {/* Month Label */}
                                    <Typography variant={selectedPeriod > 6 ? "caption" : "body1"} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                      {selectedPeriod > 6 ? month.monthName.slice(0, 3) : month.monthName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {selectedPeriod > 6 ? `'${month.year.toString().slice(-2)}` : month.year}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Detailed Monthly Breakdown - Show only first 3 months for readability */}
                    <Grid container spacing={3}>
                      {financialData.slice(0, Math.min(3, selectedPeriod)).map((month, index) => (
                        <Grid item xs={12} md={4} key={`breakdown-${month.year}-${month.month}`}>
                          <Card sx={{ 
                            height: '100%',
                            background: index === 0 
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : index === 1
                                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                                : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: 'white',
                            position: 'relative'
                          }}>
                            {index === 0 && (
                              <Chip 
                                label="🔥 Deze Maand" 
                                sx={{ 
                                  position: 'absolute', 
                                  top: 10, 
                                  right: 10, 
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }} 
                              />
                            )}
                            <CardContent>
                              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {month.monthName} {month.year}
                              </Typography>
                              
                              <Box sx={{ mt: 3 }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                                  €{month.totalRevenue.toFixed(2)}
                                </Typography>
                                
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                    📄 Totaal Documenten: {month.totalDocs}
                                  </Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                    ✅ Geëxporteerd: {month.exportedDocs}
                                  </Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                    💰 Te Factureren: {month.billableDocs}
                                  </Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                    🧾 Gefactureerd: {month.billedDocs}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                  <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                    📊 Document Omzet: €{month.documentRevenue.toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    🏢 Basis Tarief: €{MONTHLY_BASE_FEE.toFixed(2)}
                                  </Typography>
                                </Box>

                                {/* Success Rate Progress */}
                                <Box sx={{ mt: 3 }}>
                                  <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                                    Facturatie Voortgang
                                  </Typography>
                                  <Box sx={{ 
                                    width: '100%', 
                                    height: 8, 
                                    backgroundColor: 'rgba(255,255,255,0.2)', 
                                    borderRadius: 4,
                                    overflow: 'hidden'
                                  }}>
                                    <Box sx={{
                                      width: `${month.exportedDocs > 0 ? (month.billedDocs / month.exportedDocs * 100) : 0}%`,
                                      height: '100%',
                                      backgroundColor: '#4caf50',
                                      borderRadius: 4,
                                      transition: 'width 1s ease-in-out'
                                    }} />
                                  </Box>
                                  <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5 }}>
                                    {month.exportedDocs > 0 ? ((month.billedDocs / month.exportedDocs) * 100).toFixed(1) : 0}% gefactureerd
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Summary for longer periods */}
                    {selectedPeriod > 3 && (
                      <Grid container spacing={3} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                📊 Periode Samenvatting - {selectedPeriod} maanden
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                                      €{financialData.reduce((sum, month) => sum + month.totalRevenue, 0).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                                      Totale Omzet
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                                      {financialData.reduce((sum, month) => sum + month.exportedDocs, 0)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                                      Totaal Geëxporteerd
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                                      €{(financialData.reduce((sum, month) => sum + month.billableDocs, 0) * PRICE_PER_DOCUMENT).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                                      Totaal Openstaand
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                                      €{(selectedPeriod * MONTHLY_BASE_FEE).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                                      Basis Tarieven
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        📊 Geen financiële gegevens beschikbaar
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Er zijn nog geen documenten verwerkt in de afgelopen {selectedPeriod} maand{selectedPeriod > 1 ? 'en' : ''}.
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Container>
    </Box>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}


